import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Users, Sparkle, Plus } from '@phosphor-icons/react';

/**
 * StrangerPool — daily ✦ lottery for 7 random verified users.
 * Anyone can drop ✦ in. Distribution at 00:05 UTC nightly.
 * The page is a single dramatic candle.
 */
const StrangerPool = () => {
  const [status, setStatus] = useState(null);
  const [amount, setAmount] = useState(25);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/stranger-pool');
      setStatus(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const drop = async () => {
    setBusy(true);
    try {
      await api.post('/stranger-pool/contribute', { amount_resonance: Number(amount) });
      toast.success(`${amount} ✦ in the world's pool`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not contribute');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-14 pb-32" data-testid="stranger-pool">
      <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan text-center">the candle</div>
      <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter text-center mt-3">
        Stranger Pool
      </h1>
      <p className="text-white/60 text-center mt-4 max-w-lg mx-auto">
        Drop a small ✦ in. Tonight at midnight UTC, the candle splits to seven
        random bubbles you've never met. Generosity feeds generosity.
      </p>

      <div className="mt-12 relative mx-auto" style={{ maxWidth: 320 }}>
        <div className="absolute inset-0 rounded-full blur-3xl bg-bx-volt/30 animate-pulse" />
        <div className="relative grad-border p-10 text-center">
          <div className="text-[10px] uppercase tracking-widest text-bx-gold">pool tonight</div>
          <div className="font-display text-6xl font-black text-bx-volt mt-2">{status?.pool_resonance ?? '…'}</div>
          <div className="text-xs text-white/55 mt-1">✦ Resonance · from {status?.contributors ?? 0} hearts</div>
        </div>
      </div>

      <div className="mt-10 glass rounded-3xl p-6">
        <div className="text-[10px] uppercase tracking-widest text-white/45 text-center">add to the candle</div>
        <div className="mt-4 flex justify-center gap-2">
          {[5, 25, 100, 500].map((n) => (
            <button key={n}
              data-testid={`sp-quick-${n}`}
              onClick={() => setAmount(n)}
              className={`px-4 py-2 rounded-full text-sm font-bold ${
                Number(amount) === n ? 'bg-bx-volt text-black' : 'glass-hi hover:bg-white/10'
              }`}>
              {n} ✦
            </button>
          ))}
        </div>
        <button
          onClick={drop}
          disabled={busy}
          data-testid="sp-drop-btn"
          className="mt-4 w-full btn-volt px-5 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
          <Plus size={14} weight="bold" /> {busy ? 'dropping…' : `drop ${amount} ✦`}
        </button>
      </div>

      {status?.recent_payouts?.length > 0 && (
        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">recent flames</div>
          {status.recent_payouts.map((p) => (
            <div key={p.id} className="flex items-center justify-between glass-hi rounded-xl px-4 py-2 mb-2 text-sm">
              <div className="flex items-center gap-2">
                <Sparkle size={12} weight="fill" className="text-bx-volt" />
                @{p.username}
              </div>
              <div className="text-bx-volt font-bold">+{p.amount} ✦</div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-white/60 text-center mt-10 italic">
        every ✦ in and every ✦ out is signed in the public ledger.
        contributors stay anonymous unless they choose otherwise.
      </div>
    </div>
  );
};

export default StrangerPool;
